import React from 'react';
import dayjs from 'dayjs';
import { Card, CardBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { RelativeTime } from '../../ui/RelativeTime';
import { TH } from '../../ui/Table';
import { cn } from '../../../utils/cn';

// ── Skeleton loaders ────────────────────────────────────────────────────────

export const MetricSkeleton = () => (
  <Card className="metric-card">
    <CardBody className="d-flex align-items-start">
      <div className="metric-card__icon skeleton-shimmer" style={{ backgroundColor: 'var(--surface-secondary)' }}>
        <div className="skeleton-circle" />
      </div>
      <div className="metric-card__content">
        <div className="skeleton-line skeleton-title" />
        <div className="skeleton-line skeleton-value" />
        <div className="skeleton-line skeleton-subtitle" />
      </div>
    </CardBody>
  </Card>
);

export const ChartSkeleton = ({ height = 300 }) => (
  <div className="chart-skeleton" style={{ height: `${height}px` }}>
    <div className="chart-skeleton__header">
      <div className="skeleton-line skeleton-chart-title" />
    </div>
    <div className="chart-skeleton__content">
      <div className="chart-skeleton__chart">
        <div className="skeleton-chart-area" />
      </div>
    </div>
  </div>
);

// ── KPI Card ────────────────────────────────────────────────────────────────

export const KpiCard = ({ title, value, subtitle, tooltip, icon, color, loading = false, isRefreshing = false }) => {
  if (loading) return <MetricSkeleton />;
  const activeTooltip = tooltip || subtitle;
  return (
    <Card className={`metric-card${isRefreshing ? ' metric-card--refreshing' : ''}`}>
      <CardBody className="d-flex align-items-start">
        <div className="metric-card__icon" style={{ backgroundColor: `${color}20`, color }}>
          <i className={icon} />
        </div>
        <div className="metric-card__content">
          <h3 className="metric-card__title">
            {title}
            {activeTooltip && (
              <span className="dash-info-tip" data-tip={activeTooltip}>
                <i className="fas fa-info-circle" />
              </span>
            )}
          </h3>
          <div className="metric-card__value">{value}</div>
        </div>
      </CardBody>
    </Card>
  );
};

// ── Sort icon for sortable tables ───────────────────────────────────────────

export const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <i className="fas fa-sort ms-1 text-muted opacity-50" />;
  return sortDir === 'asc'
    ? <i className="fas fa-sort-up ms-1" style={{ color: '#3B82F6' }} />
    : <i className="fas fa-sort-down ms-1" style={{ color: '#3B82F6' }} />;
};

// ── Header ordenable para tablas ui/Table ───────────────────────────────────
// Flecha visible solo en la columna activa; las demás la insinúan en hover.

export const SortableTH = ({ col, sortKey, sortDir, onSort, align = 'left', children, className, ...rest }) => {
  const active = sortKey === col;
  const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TH
      onClick={() => onSort(col)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(col); }
      }}
      tabIndex={0}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cn('group cursor-pointer select-none', align === 'right' && 'text-right', className)}
      {...rest}
    >
      <span className={cn('inline-flex items-center gap-1', align === 'right' && 'justify-end')}>
        {children}
        <Icon
          size={12}
          aria-hidden
          className={active ? 'shrink-0 text-brand-600' : 'shrink-0 opacity-0 transition-opacity group-hover:opacity-60'}
        />
      </span>
    </TH>
  );
};

// ── Date preset constants ───────────────────────────────────────────────────

export const DATE_PRESETS = [
  { label: 'Hoy', getDates: () => ({ start: dayjs().format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
  { label: 'Ayer', getDates: () => ({ start: dayjs().subtract(1, 'day').format('YYYY-MM-DD'), end: dayjs().subtract(1, 'day').format('YYYY-MM-DD') }) },
  { label: 'Últ. 7 días', getDates: () => ({ start: dayjs().subtract(6, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
  { label: 'Últ. 15 días', getDates: () => ({ start: dayjs().subtract(14, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
  { label: 'Últ. 30 días', getDates: () => ({ start: dayjs().subtract(29, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
];

// ── Empty state component ───────────────────────────────────────────────────

export const EmptyState = ({ icon = 'fas fa-inbox', title, subtitle }) => {
  const { t } = useTranslation();
  return (
    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
      <i className={`${icon} fa-3x mb-3 text-muted opacity-50`} />
      <h6 className="text-muted mb-1">{title || t("Sin datos")}</h6>
      <p className="text-muted small mb-0">{subtitle || t("No hay información disponible para el período seleccionado.")}</p>
    </div>
  );
};

// ── Error state component ───────────────────────────────────────────────────

export const ErrorState = ({ onRetry, message }) => {
  const { t } = useTranslation();
  // `message` already comes translated (or straight from the backend): it must
  // not go through t() again. An array is a ValidationPipe list, joined the
  // same way errorHandler does.
  const detail = Array.isArray(message)
    ? message
        .filter((item) => typeof item === 'string' && item.trim())
        .map((item) => item.trim())
        .join(' · ')
    : (typeof message === 'string' ? message.trim() : '');
  return (
    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center" role="alert">
      <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger opacity-75" />
      <h6 className="text-danger mb-1">{t("Error al cargar los datos")}</h6>
      <p className="text-muted small mb-3">{detail || t("Ocurrió un problema al obtener la información. Verifica tu conexión.")}</p>
      {onRetry && (
        <button className="btn btn-outline-primary btn-sm" onClick={onRetry}>
          <i className="fas fa-redo me-2" />
          {t("Reintentar")}
        </button>
      )}
    </div>
  );
};

// ── Last updated indicator ──────────────────────────────────────────────────

export const LastUpdatedBadge = ({ timestamp, isRefreshing }) => {
  const { t } = useTranslation();
  if (!timestamp && !isRefreshing) return null;
  return (
    <div className="d-flex align-items-center gap-2 text-muted small">
      {isRefreshing ? (
        <>
          <span className="spinner-border spinner-border-sm" role="status" />
          <span>{t("Actualizando…")}</span>
        </>
      ) : timestamp ? (
        <>
          <i className="fas fa-clock" />
          <span>{t("Actualizado")} <RelativeTime value={timestamp} live t={t} /></span>
        </>
      ) : null}
    </div>
  );
};

// ── Color palette constants ─────────────────────────────────────────────────

export const COLORS = {
  primary: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#8B5CF6',
  secondary: '#6B7280',
};

export const CHART_PALETTE = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#6B7280', '#EC4899', '#14B8A6', '#F97316', '#06B6D4',
];
